// Transport seam for the risk module. Two independent data sources feed this
// tab and must not be confused:
//
//  - The indicator CATALOG (label/available/presets/threshold defaults) comes
//    from `GET /risk`.
//  - The weekly PROBABILITY data (multi-year climatology, years_with_event,
//    peak_weeks, etc.) comes from `GET /risk/probability`, implemented in
//    risk/climatology.py. Both are live; the bundled fixtures below are a dev
//    convenience for reaching states real data won't reproduce on demand
//    (small_sample, no_data, outside_coverage), selected by
//    RISK_PROBABILITY_SOURCE.
//
// The climatology window is whatever the server reports in
// provenance.climatology_window — it advances every January (1981 through the
// last closed calendar year), so never hard-code its width here or in copy.
//
// IMPORTANT: the other compute endpoint, POST /risk/<risk>, CANNOT produce the
// probability numbers this screen needs — its `frequency` is "fraction of
// days in the requested window that crossed the threshold", not "fraction of
// climatology years in which at least one day crossed". Different
// denominators, not convertible. Do not wire POST /risk/<risk> into the
// probability path as an "optimization".

import axios from 'axios';
import {
    RISK_API_BASE,
    RISK_PROBABILITY_SOURCE,
    RISK_PROBABILITY_BASE,
    RISK_PROBABILITY_PATH,
    RISK_TIMEOUT_MS,
    RISK_PROBABILITY_TIMEOUT_MS,
    RISK_FIXTURE_LATENCY_MS,
} from '@/config/riskConfig';

export function fetchCatalog({ signal } = {}) {
    return axios.get(`${RISK_API_BASE}/risk`, { timeout: RISK_TIMEOUT_MS, signal });
}

export function fetchProbability(params, { signal } = {}) {
    if (RISK_PROBABILITY_SOURCE === 'fixtures') {
        return fetchProbabilityFixture(params, { signal });
    }
    return axios.get(`${RISK_PROBABILITY_BASE}${RISK_PROBABILITY_PATH}`, {
        params,
        // Its own, much longer timeout — NOT the catalog's. See riskConfig.js.
        timeout: RISK_PROBABILITY_TIMEOUT_MS,
        signal,
    });
}

// No server-side CSV export exists: risk/routes.py registers /risk and
// /risk/<risk> and /risk/probability, and nothing else. This used to build a
// "<path>.csv" URL that downloadRiskCsv preferred over the client-side builder,
// so in live mode the export button downloaded a 404 page. The builder in
// riskCsv.js is the export — it works in both modes and matches the format in
// the design handoff's api/export.sample.csv.

function delay(ms, signal) {
    return new Promise((resolve, reject) => {
        const t = setTimeout(resolve, ms);
        if (signal) {
            signal.addEventListener('abort', () => {
                clearTimeout(t);
                const err = new Error('canceled');
                err.code = 'ERR_CANCELED';
                reject(err);
            });
        }
    });
}

// Dev-only routing so every state (empty, small-sample, outside-coverage) is
// reachable with no backend. Kept in one function so deleting it later, once
// the real endpoint exists, is a one-line change.
function pickScenario(params) {
    const lat = Number(params.lat);
    const lon = Number(params.lon);
    if (Number.isFinite(lat) && Number.isFinite(lon)) {
        if (lat < 24 || lat > 50 || lon < -125 || lon > -66) return 'outside_coverage';
    }
    const startYear = Number(String(params.start_date || '').slice(0, 4));
    if (startYear > new Date().getFullYear()) return 'no_data';
    if (Number(params.threshold) <= 20) return 'small_sample';
    return 'freeze_weekly';
}

const FIXTURE_FILES = {
    freeze_weekly: () => import(/* webpackChunkName: "risk-fixtures" */ './fixtures/freeze.weekly.json'),
    small_sample: () => import(/* webpackChunkName: "risk-fixtures" */ './fixtures/small-sample.json'),
    no_data: () => import(/* webpackChunkName: "risk-fixtures" */ './fixtures/no-data.json'),
};

async function fetchProbabilityFixture(params, { signal } = {}) {
    await delay(RISK_FIXTURE_LATENCY_MS, signal);

    const scenario = pickScenario(params);

    if (scenario === 'outside_coverage') {
        const mod = await import(/* webpackChunkName: "risk-fixtures" */ './fixtures/outside-coverage.json');
        const body = mod.default || mod;
        const err = new Error('outside_coverage');
        err.response = { status: 422, data: body };
        throw err;
    }

    const mod = await FIXTURE_FILES[scenario]();
    const body = mod.default || mod;
    // Deep-clone so callers can safely mutate the normalized copy without
    // corrupting the cached module export on a later re-import.
    return { status: 200, data: JSON.parse(JSON.stringify(body)) };
}
